import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import {
    Button, CloseIcon, GapChip, IconButton,
} from '../ui';
import { formatCurrency } from '../../utils/formatting';
import { aiPriceState } from '../../utils/aiPrice';
import { unitAgingTone } from '../../utils/dealershipSettings';
import { useDealershipSettings } from '../../contexts/settingsContext';
import {
    dueInfo, prettifyPerson, priceCut, TYPE_LABELS, typeKey, unitDisplayName, unitYear,
} from '../../utils/taskBoard';
import { fmtActivityDate, splitDetails } from '../../utils/unitActivities';
import AiDescription from '../UnitV2/tabs/AiDescription';
import { RiskPill } from '../InventoryV2/InventoryTableV2';
import { Avatar } from './TaskCardV2';

const NEW_USED = { N: 'New', U: 'Used' };

// The unit's own tabs the preview links to (G-04: each to its own tab).
export const PREVIEW_LINKS = Object.freeze([
    { id: 'ai', label: 'Intelligence' },
    { id: 'pricing', label: 'Pricing & Usage' },
]);

/**
 * The card preview (T2, D10): the unit's key figures from the inventory the
 * board already holds, the task, and the ways into the unit. "Open unit ·
 * Activity tab" goes to the unit page on Activity, as a card click did
 * before. Rendered beside the board, or in a drawer below 1400 px (the page
 * decides); the close button is left out in the drawer, which has its own.
 */
const TaskPreview = forwardRef(({
    task, unit, assigneeName, onOpen, onClose,
}, openRef) => {
    const { aging, gapThresholds } = useDealershipSettings();
    const type = typeKey(task.type);
    const due = dueInfo(task);
    const cut = task.type === 'price_change' ? priceCut(task) : null;
    const year = unitYear(unit);
    const meta = [NEW_USED[unit.NewUsedStatus], unit.CATEGORY].filter(Boolean).join(' · ');
    const who = task.assigned_to ? (assigneeName || prettifyPerson(task.assigned_to)) : 'Unassigned';
    const days = unit.DaysInStock != null ? Number(unit.DaysInStock) : null;
    // The same split as the Activity tab (P-TASK-087, QA T-2).
    const { rationale, aiDescription } = splitDetails(task.details);

    return (
        <div className="tb2-pv">
            <div className="tb2-pv-head">
                <div className="tb2-pv-top">
                    <span className="tb2-pv-stock">#{unit.StockNumber}</span>
                    <i className={`tb2-age v2-tone-${unitAgingTone(unit, aging)}`} aria-hidden="true" />
                    <span className="tb2-muted">
                        {[unit.BranchName, days != null && Number.isFinite(days) ? `${days.toLocaleString('en-US')} ${days === 1 ? 'day' : 'days'}` : null].filter(Boolean).join(' · ')}
                    </span>
                    {onClose && (
                        <IconButton label="Close preview" className="tb2-pv-close" onClick={onClose}>
                            <CloseIcon />
                        </IconButton>
                    )}
                </div>
                <h3>
                    {unitDisplayName(unit)}
                    {year && <span className="tb2-muted">{` ${year}`}</span>}
                </h3>
                {meta && <span className="tb2-muted tb2-pv-meta">{meta}</span>}
            </div>

            <dl className="tb2-pv-grid">
                <div><dt>Asking</dt><dd className="tb2-pv-n">{unit.Best_Sell_Price > 0 ? formatCurrency(unit.Best_Sell_Price) : '—'}</dd></div>
                <div>
                    <dt>AI price</dt>
                    <dd className="tb2-pv-n">{Number(unit.PredictedPrice) > 0 ? formatCurrency(unit.PredictedPrice) : '—'}</dd>
                    {aiPriceState(unit, gapThresholds) !== 'none' && <dd><GapChip unit={unit} /></dd>}
                </div>
                <div><dt>Days in stock</dt><dd className="tb2-pv-n">{days != null && Number.isFinite(days) ? days.toLocaleString('en-US') : '—'}</dd></div>
                <div><dt>Sell-speed risk</dt><dd>{unit.RiskTier ? <RiskPill unit={unit} /> : <span className="tb2-muted">Not scored</span>}</dd></div>
            </dl>

            <div className="tb2-pv-task">
                <div className="tb2-pv-tt">
                    <span className={`tb2-type tb2-type--${type}`}>{TYPE_LABELS[type]}</span>
                    {due && <span className={`tb2-due tb2-due--${due.tone}`}>{due.text}</span>}
                </div>
                <b className="tb2-pv-title">{task.title}</b>
                {cut && (
                    <div className="tb2-pchange">
                        <s>{formatCurrency(cut.from)}</s>
                        <span className="tb2-arr" aria-hidden="true">→</span>
                        <span className="tb2-to">{formatCurrency(cut.to)}</span>
                        {cut.change !== 0 && <span className={`tb2-delta${cut.change > 0 ? ' is-up' : ''}`}>{cut.label}</span>}
                    </div>
                )}
                {rationale && <p className="tb2-pv-details">{rationale}</p>}
                {aiDescription && <AiDescription text={aiDescription} />}
                <div className="tb2-pv-who">
                    <Avatar email={task.assigned_to} name={who} size="sm" />
                    <span>{`${who} · created ${fmtActivityDate(task.created_at)} by ${task.created_by || '—'}`}</span>
                </div>
            </div>

            <div className="tb2-pv-links">
                <Button variant="primary" ref={openRef} onClick={() => onOpen('activity')}>Open unit · Activity tab</Button>
                {PREVIEW_LINKS.map((l) => (
                    <Button key={l.id} onClick={() => onOpen(l.id)}>
                        {l.label} <span aria-hidden="true">›</span>
                    </Button>
                ))}
            </div>
        </div>
    );
});

TaskPreview.displayName = 'TaskPreview';

TaskPreview.propTypes = {
    task: PropTypes.object.isRequired,
    unit: PropTypes.object.isRequired,
    assigneeName: PropTypes.string,
    onOpen: PropTypes.func.isRequired,
    onClose: PropTypes.func,
};

export default TaskPreview;
